/**
 * HESAP KAPSAMI — sayfaların "bütün defter" yerine seçilen hesap(lar) üzerinde
 * çalışması (back-test kararı K2' III, 14.10.2026).
 *
 * "BÜTÜN DEFTER" = GERÇEK HESAPLAR. Sanal hesap yalnızca açıkça seçilince
 * görünür ve tek başına değerlenir (motor iki türü karıştırmaz).
 *
 * SON SEÇİM YALNIZCA BELLEKTE: hesap kimlikleri profilin şifreli kabının
 * dışına (localStorage) yazılmaz (CLAUDE.md §8). Sayfa yenilenince bütün
 * deftere döner.
 */

import { useCallback } from "react";
import { sanalHesapMi, type Account, type HesapRengi, type LocalRepository, type SanalHesapTanimi } from "@parafon/contracts/local-api";
import { useAsync } from "../repo/useAsync";

export interface KapsamSecenegi {
  readonly id: string;
  readonly ad: string;
  readonly renk?: HesapRengi;
  readonly sanal: boolean;
  /** Sanal hesap kuralı temettüyü yok sayıyor (19.10.2026). */
  readonly temettuHaric?: boolean;
}

/** Bütün defter (gerçek hesaplar) — seçim listesinde boş değer. */
export const BUTUN_DEFTER = "";

let raporKapsami: string = BUTUN_DEFTER;
let grafikKapsami: readonly string[] = [];

export const kapsamBellegi = {
  rapor: (): string => raporKapsami,
  raporYaz: (id: string): void => {
    raporKapsami = id;
  },
  grafik: (): readonly string[] => grafikKapsami,
  grafikYaz: (ids: readonly string[]): void => {
    grafikKapsami = ids;
  },
  /** Sanal hesap sayfasından "Varlık Dökümü'nde / Günlük Analiz'de aç". */
  ac: (id: string): void => {
    raporKapsami = id;
    grafikKapsami = [id];
  },
  sifirla: (): void => {
    raporKapsami = BUTUN_DEFTER;
    grafikKapsami = [];
  },
};

export function kapsamSecenekleri(
  hesaplar: readonly Account[],
  sanallar: readonly SanalHesapTanimi[],
): KapsamSecenegi[] {
  return [
    ...hesaplar
      .filter((a) => !a.deleted)
      .map((a) => ({ id: a.id, ad: a.name, ...(a.renk === undefined ? {} : { renk: a.renk }), sanal: false })),
    ...sanallar.map((s) => ({
      id: s.id,
      ad: s.ad,
      ...(s.renk === undefined ? {} : { renk: s.renk }),
      sanal: true,
      ...(s.temettu === "yokSay" ? { temettuHaric: true } : {}),
    })),
  ];
}

export interface KapsamVerisi {
  readonly secenekler: readonly KapsamSecenegi[];
  readonly sanallar: readonly SanalHesapTanimi[];
  readonly yukleniyor: boolean;
}

export function useKapsamSecenekleri(repo: LocalRepository, revision: number): KapsamVerisi {
  const yukle = useCallback(async () => {
    const [hesaplar, sanallar] = await Promise.all([
      repo.accounts.list(),
      repo.sanalHesaplar === undefined ? Promise.resolve([] as readonly SanalHesapTanimi[]) : repo.sanalHesaplar.liste(),
    ]);
    return { hesaplar, sanallar };
  }, [repo]);
  const { state } = useAsync(yukle, [repo, revision]);
  const veri = state.data;
  return {
    secenekler: veri === undefined ? [] : kapsamSecenekleri(veri.hesaplar, veri.sanallar),
    sanallar: veri?.sanallar ?? [],
    yukleniyor: veri === undefined,
  };
}

/** Seçilen kapsamın değerleme isteğine giden hesap listesi. */
export function kapsamHesaplari(id: string): readonly string[] | undefined {
  return id === BUTUN_DEFTER ? undefined : [id];
}

export { sanalHesapMi };
